<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<Header theme="primary" logo-align="center" />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:20691: 24 under the 56px logo row, 24 between the blocks, cards stacked 8 apart.
			     A plan that is not a commitment keeps the page as it was before DEV-35727 (owner, 2026-10-22). -->
			<LayoutFlex
				:padding="planCard ? 'var(--size-24) var(--size-20)' : 'var(--size-8) var(--size-20) var(--size-24)'"
				gap="var(--size-24)"
			>
				<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
					<Text font="--heading-h3" color="--text-dark-primary">Subscription details</Text>
					<Text font="--text-16-22-reg" :color="planCard ? KOVA.textSecondary : '--text-dark-secondary'">
						<template v-if="userName">{{ userName }} ·</template>
						{{ userEmail || '—' }}
					</Text>
				</LayoutFlex>

				<template v-if="store.loading && !store.hasSubscription">
					<Text font="--text-16-22-reg" color="--text-dark-secondary">Loading…</Text>
				</template>

				<template v-else-if="store.error && !store.hasSubscription">
					<LayoutFlex
						padding="var(--size-16)"
						gap="var(--size-12)"
						background="--background-white"
						corner-radius="var(--size-24)"
					>
						<Text font="--text-18-22-bold" color="--text-dark-primary">Couldn’t load your subscription</Text>
						<Text font="--text-14-20-reg" color="--text-dark-secondary">{{ store.error }}</Text>
						<Button hierarchy="secondary" full-width @ds:click="refresh">
							<Text font="--text-16-18-semi">Try again</Text>
						</Button>
					</LayoutFlex>
				</template>

				<LayoutFlex v-else-if="store.hasSubscription" padding="0" :gap="planCard ? 'var(--size-8)' : 'var(--size-24)'">
					<!-- A commitment plan — monthly or paid in full — has its own card (DEV-35727); every other
					     plan, under review and rejected too, keeps the classic one (and its own spacing, from
					     the older design). -->
					<CommitmentCard
						v-if="planCard"
						:can-manage="canManageSubscription"
						@manage="goCancel"
						@change-payment-method="goChangePaymentMethod"
						@pay-and-cancel="goPayAndCancel"
					/>
					<SubscriptionCard
						v-else
						:can-manage="canManageSubscription"
						@manage="goCancel"
						@change-payment-method="goChangePaymentMethod"
					/>

					<!-- Figma 7415:20768: every frame of the plan card has it, between the card and support —
					     padding 24, the button 8 below the 16 gap. The classic card's older page has none. -->
					<LayoutFlex
						v-if="planCard"
						padding="var(--size-24)"
						gap="var(--size-16)"
						background="--background-white"
						corner-radius="var(--size-36)"
					>
						<Text font="--text-18-22-reg" color="--text-dark-primary">Have medical questions?</Text>
						<LayoutFlex padding="var(--size-8) 0 0" gap="0">
							<Button hierarchy="secondary" full-width @ds:click="contactClinician">
								<Text font="--text-16-18-semi">Contact clinician</Text>
							</Button>
						</LayoutFlex>
					</LayoutFlex>

					<!-- Figma 7415:20772: padding 24, 16 between the lines, the button 8 lower still. -->
					<LayoutFlex
						v-if="planCard"
						padding="var(--size-24)"
						gap="var(--size-16)"
						background="--background-white"
						corner-radius="var(--size-36)"
					>
						<Text font="--text-18-22-reg" color="--text-dark-primary">Have questions or need assistance?</Text>
						<Text font="--text-14-20-med" :color="KOVA.textSecondary">
							Contact support if you have issues or questions about your subscription. We’re here for you.
						</Text>
						<LayoutFlex padding="var(--size-8) 0 0" gap="0">
							<Button hierarchy="secondary" full-width @ds:click="contactSupport">
								<Text font="--text-16-18-semi">Contact support</Text>
							</Button>
						</LayoutFlex>
					</LayoutFlex>
					<!-- The classic card's page: the support card as it was before DEV-35727. -->
					<LayoutFlex
						v-else
						padding="var(--size-12)"
						gap="0"
						background="--background-white"
						corner-radius="var(--size-36)"
					>
						<LayoutFlex padding="var(--size-16)" gap="var(--size-16)">
							<Text font="--text-18-22-reg" color="--text-dark-primary">Have questions or need assistance?</Text>
							<Text font="--text-14-20-med" color="--text-dark-secondary">
								Contact support if you have issues or questions about your subscription. We’re here for you.
							</Text>
							<Button hierarchy="secondary" full-width @ds:click="contactSupport">
								<Text font="--text-16-18-semi">Contact support</Text>
							</Button>
						</LayoutFlex>
					</LayoutFlex>
				</LayoutFlex>

				<template v-else>
					<LayoutFlex
						padding="var(--size-16)"
						gap="var(--size-12)"
						background="--background-white"
						corner-radius="var(--size-24)"
					>
						<Text font="--text-18-22-reg" color="--text-dark-primary">You don’t have an active subscription</Text>
					</LayoutFlex>
				</template>

				<!-- In the content flow, not #footer: since DS ≥0.39 the footer slot sits outside
				     the screen's scroll area (always pinned to the viewport bottom), while the
				     Figma places Log out after the support card — scrolled to, not pinned. -->
				<Button hierarchy="secondary" full-width @ds:click="logout">
					<Text font="--text-16-18-semi">Log out</Text>
				</Button>
			</LayoutFlex>

			<!--
			  "Before you go…" (Figma 30320:18993) — interstitial shown instead of navigating
			  straight into the cancel flow while the prescription is still under clinical review.
			  The DS Modal's own dialog is left transparent and the card is rebuilt in the slot:
			  the design's 36px radius / 16-24 padding differ from the DS defaults, and the dialog
			  is `Teleport`ed to <body>, so a `:deep()` override from this scoped block would never
			  match it. Slot content, by contrast, is compiled in this component's scope.
			-->
			<Modal :visible="showBeforeYouGo" animation="fade" background="transparent" @ds:close="dismissBeforeYouGo">
				<div class="status__modal-card">
					<button type="button" class="status__modal-close" aria-label="Close" @click="dismissBeforeYouGo">
						<Icon type="ds_close" size="--size-24" />
					</button>
					<LayoutFlex padding="0" gap="var(--size-32)">
						<LayoutFlex padding="0" gap="var(--size-24)" align-items="flex-start">
							<Text font="--heading-h2" color="--text-dark-primary">Before you go…</Text>
							<Text font="--text-16-22-reg" color="--text-dark-primary">
								A clinician is reviewing your medical intake to approve your prescription. (This usually takes around 3
								hours.)
							</Text>
							<Text font="--text-16-22-reg" color="--text-dark-primary">
								Waiting until the review is complete will give you more flexibility if you still decide to cancel.
							</Text>
						</LayoutFlex>
						<LayoutFlex padding="0" gap="var(--size-8)">
							<Button full-width @ds:click="dismissBeforeYouGoViaGotIt">
								<Text font="--text-16-18-semi">Got it</Text>
							</Button>
							<Button hierarchy="secondary" full-width @ds:click="continueCancellation">
								<Text font="--text-16-18-semi">Continue with cancellation</Text>
							</Button>
						</LayoutFlex>
					</LayoutFlex>
				</div>
			</Modal>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Header, Icon, LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CommitmentCard from '@/components/status/CommitmentCard.vue'
import SubscriptionCard from '@/components/status/SubscriptionCard.vue'
import { useAuthStore } from '@/store/auth'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { KOVA } from '@/designTokens'
import { COMMITMENT_ROUTES, entryRoute } from '@/cancelFlow/commitment'
import { expirationDate, planDetails } from '@/analytics/plan'
import { flushBeforeLeaving } from '@/analytics/client'
import {
	trackBeforeYouGoContinueClick,
	trackBeforeYouGoGotItClick,
	trackBeforeYouGoPopupClose,
	trackBeforeYouGoPopupView,
	trackChangePaymentMethodClick,
	trackContactClinicianClick,
	trackLogoutClick,
	trackManageSubscriptionClick,
	trackPayRemainingBalanceClick,
	trackSubscriptionDetailsView,
	trackSupportEntryPoint,
} from '@/analytics/events'
import { SUPPORT_EMAIL } from '@/config'

const router = useRouter()
const store = useSubscriptionStore()
const auth = useAuthStore()
const survey = useSurveyStore()
const cancelFlow = useCancelFlowStore()

const showBeforeYouGo = ref(false)

// The name comes ONLY from the med-users business profile — see `displayName` in the auth store for why
// there is no Auth0 fall-back. With no profile name it is null and the name is omitted — the separator
// lives inside the `v-if`, so the line collapses to just the email.
const userName = computed(() => auth.displayName)
const userEmail = computed(() => auth.displayEmail)

// `cancelled` and `declined` are terminal "already cancelled" states (declined = prescription
// rejected → refunded; tracked off the status string since its `cancelled` flag isn't guaranteed
// — see store `checkStatus.isDeclined`). For them /status drops the affordances that imply an
// ongoing subscription: the "Manage subscription" button (it opens the cancel flow) and the
// "Charge Date" row (there's no upcoming charge). Other states (active, awaiting_approval, …) keep both.
// Decided here once and handed to whichever card renders (`can-manage`).
const isCancelledOrDeclined = computed(() => {
	const s = store.checkStatus
	return s.isCancelled || s.isDeclined
})

const canManageSubscription = computed(() => !isCancelledOrDeclined.value)

// Only a commitment (monthly or paid in full) gets the plan card — under review and rejected too. A plan
// that is not a commitment keeps the classic card and page in every state, as before DEV-35727 (owner,
// 2026-10-22; from 2026-10-14 every plan under review or rejected had the plan card, ADR 0017 decision 8).
const planCard = computed(() => Boolean(cancelFlow.kind))

function toEpochMs(iso: string | null | undefined): number | undefined {
	if (!iso) return undefined
	const t = new Date(iso).getTime()
	return Number.isNaN(t) ? undefined : t
}

function analyticsStatus(): string | undefined {
	const s = store.checkStatus
	// `declined` first: it's a status-string state that may also carry the cancelled flag,
	// so report it distinctly rather than collapsing it into 'cancelled'/'active'.
	if (s.isDeclined) return 'declined'
	// A plan cancelled but still running out (a commitment wind-down) reports `cancelled` (CR 72).
	if (s.isCancelled) return 'cancelled'
	// Otherwise billing's own status — `awaiting_approval`, `past_due`, … (CR 72), not collapsed to `active`.
	if (store.subscription?.status) return store.subscription.status
	if (s.isPaused) return 'paused'
	if (s.isActive) return 'active'
	if (s.isExpired) return 'expired'
	if (s.isOverdue) return 'past_due'
	return undefined
}

onMounted(() => {
	const sub = store.subscription
	const phase = store.getActivePhase ?? store.getPhases.regular
	const price = phase?.price
	trackSubscriptionDetailsView({
		status: analyticsStatus(),
		// Same value the "Rx Status" pill renders; omitted entirely when the Rx state is unknown.
		rx_status: store.getRxStatus,
		is_trial: store.checkPeriodType.isTrial,
		subscription_id: sub?.subscription_id,
		product_id: sub?.product_id,
		renewal_date: toEpochMs(sub?.current_period_end_at),
		expiration_date: sub ? toEpochMs(expirationDate(sub, store.checkStatus.isCancelled)) : undefined,
		last_billed_date: toEpochMs(sub?.last_charge_at),
		subscription_duration:
			phase?.interval_duration && phase?.interval_unit
				? `${phase.interval_duration} ${phase.interval_unit}`
				: undefined,
		sub_price_usd: typeof price === 'number' ? price.toFixed(2) : undefined,
		// The plan and, on a commitment, its term / payments / balance (AL-15098, CR 72).
		// Only the first prescription's review hides the balance: a refill in review keeps the approved plan's.
		...(sub ? planDetails(sub, cancelFlow.stage === 'review') : {}),
	})
})

// While the prescription is under clinical review the "Manage subscription" button opens the
// "Before you go…" interstitial instead of entering the survey directly — the user can either
// wait for the review ("Got it") or push on. Any other Rx state keeps the original behaviour and
// goes straight to the first survey step.
function goCancel() {
	trackManageSubscriptionClick()
	// A commitment plan takes the DEV-35727 flow (Figma "TO BE / Hard cancel"), which has no
	// "Before you go…" interstitial: under review it opens on /cancel/scope, approved on the survey.
	// `cancelFlow.reset()` clears the previous attempt's receipt for the same reason as `startFlow()`.
	const stage = cancelFlow.stage
	if (stage) {
		survey.startFlow()
		cancelFlow.reset()
		router.push(entryRoute(stage))
		return
	}
	if (store.isRxUnderReview) {
		showBeforeYouGo.value = true
		trackBeforeYouGoPopupView()
		return
	}
	// Entry into the flow is where the survey is cleared — nothing clears it on the way *out*
	// (× / Back / closed tab), so an abandoned attempt would otherwise hand its answers to this
	// one. Most of those answers are analytics-only, but `cancellation_scope` is not: a stale
	// "cancel plan and prescription" would make this cancel `hard`. See `survey.startFlow()`.
	survey.startFlow()
	cancelFlow.reset()
	router.push('/cancel/reasons')
}

// Every dismissal path (× / backdrop / "Got it") lands here, so this is where `PopupClose` belongs.
// "Got it" additionally reports its own ButtonClick via `dismissBeforeYouGoViaGotIt` — the two
// events are complementary, not duplicates: × / backdrop produce only the close.
function dismissBeforeYouGo() {
	showBeforeYouGo.value = false
	trackBeforeYouGoPopupClose()
}

function dismissBeforeYouGoViaGotIt() {
	trackBeforeYouGoGotItClick()
	dismissBeforeYouGo()
}

// Not a dismissal — the user is moving on into the flow, so no `PopupClose` here (see
// `trackBeforeYouGoPopupClose` for why the close event is kept to the save path).
function continueCancellation() {
	trackBeforeYouGoContinueClick()
	showBeforeYouGo.value = false
	// The flow's other entry point, so the same fresh-pass reset as `goCancel()`. Dismissing the
	// modal deliberately doesn't reset: the user never entered the flow.
	survey.startFlow()
	cancelFlow.reset()
	// Only non-commitments reach the modal now — a commitment under review goes straight to
	// /cancel/scope from `goCancel()` (DEV-35727).
	router.push('/cancel/scope')
}

// A commitment cancelled in this flow (after "Keep my plan" or the instalments) can still be ended
// today by paying what's left — straight to the pay screen, with a fresh receipt.
function goPayAndCancel() {
	trackPayRemainingBalanceClick()
	cancelFlow.reset()
	router.push(COMMITMENT_ROUTES.endNow)
}

function goChangePaymentMethod() {
	trackChangePaymentMethodClick()
	// The history-state marker lets the paywall distinguish this entry from a direct
	// deep link (entry_point analytics): it survives a refresh and stays out of the URL.
	router.push({ path: '/payment/update', state: { entry: 'subscription_details_button' } })
}

function contactSupport() {
	trackSupportEntryPoint('web_subscription_details')
	window.location.href = `mailto:${SUPPORT_EMAIL}`
}

// Interim: there is no clinician address or app deeplink yet, so "Contact clinician" opens the
// support mail, which passes medical questions on.
function contactClinician() {
	trackContactClinicianClick()
	window.location.href = `mailto:${SUPPORT_EMAIL}`
}

async function logout() {
	trackLogoutClick()
	// The Auth0 redirect unloads the page before the SDK's next send.
	await flushBeforeLeaving()
	auth.signOut()
}

function refresh() {
	store.fetchSubscription()
}
</script>

<style scoped>
/*
  Most styling comes from the growth-ds design system (LayoutScreen / LayoutFlex /
  Button / Text). The subscription cards style themselves (components/status/); what is
  left here is the "Before you go…" modal.
*/

/* "Before you go…" card. The DS Modal's dialog is transparent (see the template comment), so the
   card's surface, radius and padding live here. */
.status__modal-card {
	position: relative;
	background: var(--background-white);
	border-radius: var(--size-36);
	padding: var(--size-24) var(--size-16);
}

/* DS ships no icon-only button variant, so this mirrors CancelHeader's native button. */
.status__modal-close {
	position: absolute;
	top: var(--size-20);
	right: var(--size-20);
	appearance: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	background: none;
	border: none;
	color: var(--text-dark-tertiary);
	cursor: pointer;
}
</style>
